/**
 * Inverio Store — Backward-Compatible Facade
 *
 * This module re-exports all types and provides a unified `useInverioStore`
 * hook that delegates to focused domain stores:
 *
 *   - profile-store.ts  — user identity, roles, onboarding, profiles, team
 *   - score-store.ts    — scores, recommendations, matches, DD conclusions
 *   - dealflow-store.ts — dealflow pipeline, portfolio, commitments
 *   - dataroom-store.ts — documents, checklist
 *
 * The messaging domain (messaging-store.ts) has been fully migrated off this
 * facade — screens use React Query hooks + messaging-mappers directly. The
 * facade only still resets messaging-store in the sign-out cascade below.
 *
 * Existing consumers can continue to `import { useInverioStore, SomeType }
 * from '@/lib/state/inverio-store'` without any changes.
 *
 * New code should prefer importing directly from the domain store.
 */

import { create } from 'zustand';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { reportError } from '@/lib/errors';

// Domain stores
import { useProfileStore } from './profile-store';
import { useScoreStore } from './score-store';
import { useDealflowStore } from './dealflow-store';
import { useMessagingStore } from './messaging-store';
import { useDataroomStore } from './dataroom-store';
import { useSubscriptionStore } from './subscription-store';
import { useScoreHistoryStore } from './score-history-store';
import { useUsageStore } from './usage-store';
// Feature-folder stores — persisted + user-scoped, so they MUST reset on
// sign-out. They live outside src/lib/state/, which is exactly why they were
// historically missed (see .planning/LEARNINGS.md "Produced but not wired").
import { useReferralStore } from '@/lib/referral/referral-store';
import { useTeamStore } from '@/lib/team/team-store';
import { useThesisStore } from '@/lib/thesis/thesis-store';
import { useDataRoomStore } from '@/lib/dataroom/store';
import { useGoogleDriveStore } from '@/lib/google-drive/store';
import { useDropboxStore } from '@/lib/dropbox/store';

// ============================================================================
// Re-export ALL types so existing imports continue to work
// ============================================================================

export type {
  UserRole,
  OnboardingStep,
  OnboardingProgress,
  FundraisingRound,
  InvestmentRecord,
  PortfolioClaim,
  VerificationRequest,
  StartupUpdate,
  UpdateVisibility,
  InvestorBio,
  InvestmentThesis,
  InvestorValueAdd,
  InvestorEngagement,
  InvestorDDActivity,
  InvestorPatterns,
  FounderTestimonial,
  InvestorSocialLinks,
  InvestorBadge,
  InvestorProfile,
  UserInfo,
  TeamMemberRole,
  StartupTeamMember,
} from './profile-store';

export {
  STARTUP_REQUIRED_STEPS,
  STARTUP_OPTIONAL_STEPS,
  INVESTOR_REQUIRED_STEPS,
  INVESTOR_OPTIONAL_STEPS,
  VOICE_INPUT_STEPS,
} from './profile-store';

export type { InvestorBadgeType } from './profile-store';

// Scoring-domain types (ScoreDimension, EvidenceLevel, SubCriterionScore,
// ScoreCriteria, AIRecommendation) are intentionally NOT re-exported here — import
// them from their definition module `@/lib/scoring/types`. Keeping them out of this
// aggregator gives each type a single export site and prevents tooling from
// resolving a phantom scoring->state dependency through this barrel.
export type { InvestorMatch } from './score-store';

export type {
  DealflowStartup,
  PortfolioUpdate,
  PortfolioCompany,
  CommitmentType,
  CommitmentSource,
  CommitmentStatus,
  InvestorCommitment,
  ExternalCommitment,
  CommitmentSummary,
  CommitmentSettings,
  InvestorCommitmentMetrics,
} from './dealflow-store';

export type {
  DataRoomDocument,
  DocumentComment,
  RequiredDocumentType,
} from './dataroom-store';

export { REQUIRED_DOCUMENTS } from './dataroom-store';

// ============================================================================
// One-time storage key migration (invescore-storage -> inverio-storage)
// Kept here for backward compat — runs once before first store access
// ============================================================================

async function migrateStorageKey() {
  if (typeof window === 'undefined') return; // Skip during SSR
  try {
    const oldData = await AsyncStorage.getItem('invescore-storage');
    if (oldData && !(await AsyncStorage.getItem('inverio-storage'))) {
      await AsyncStorage.setItem('inverio-storage', oldData);
      await AsyncStorage.removeItem('invescore-storage');
    }
  } catch (err) { reportError(err, { context: 'migrateStorageKey', silent: true }); }
}
migrateStorageKey(); // Fire and forget

// ============================================================================
// Unified facade store
//
// This is a vanilla (non-persisted) zustand store whose sole purpose is to
// present the same flat API that the old monolithic store exposed.
// Every getter reads from the appropriate domain store; every setter
// delegates to the appropriate domain store.
// ============================================================================

/**
 * Unified InverioState interface — kept for backward compatibility.
 * Matches the original 1:1 so existing selectors continue to work.
 */
interface InverioState {
  _hasHydrated: boolean;

  // Profile domain — migrated to useProfileStore (@/lib/state/profile-store).
  // No facade members remain; consumers read directly from the domain store.

  // Score domain — migrated to useScoreStore (@/lib/state/score-store).
  // No facade members remain; consumers read directly from the domain store.

  // Dealflow domain — migrated to useDealflowStore (@/lib/state/dealflow-store).
  // Only these members remain on the facade: `convertSoftToHard` still has a
  // facade consumer, and these carry facade-injected dependencies / narrower
  // signatures. Everything else now reads directly from the domain store.
  updateDealflowStartup: (id: string, updates: Partial<import('@/lib/supabase/edge-functions').StartupSummary>) => void;
  convertSoftToHard: (id: string, finalAmount: number, conclusionId?: string) => void;
  closeFundraisingRound: (outcome: 'completed' | 'cancelled') => void;

  // Reset
  reset: () => void;
}

// ============================================================================
// Facade store creation
// ============================================================================

/**
 * @deprecated Prefer importing from domain stores directly (profile-store,
 * score-store, dealflow-store, messaging-store, dataroom-store).
 *
 * This unified hook is kept purely for backward compatibility so the 68+
 * existing consumer files continue to work without changes.
 */
export const useInverioStore = create<InverioState>()(
  (set, get) => ({
    // ---- Hydration ----
    get _hasHydrated() {
      // Hydrated when all persisted domain stores have hydrated
      return useProfileStore.getState()._hasHydrated && useDealflowStore.getState()._hasHydrated;
    },

    // ---- Profile domain delegates ----
    // Migrated to useProfileStore. Consumers read directly from the domain store;
    // only the hydration gate and the reset cascade below still reference useProfileStore.

    // ---- Score domain delegates ----
    // Migrated to useScoreStore. Consumers read directly from the domain store;
    // only the reset cascade below still references useScoreStore.

    // ---- Dealflow domain delegates (residual — see interface note) ----
    updateDealflowStartup: (id, updates) => useDealflowStore.getState().updateDealflowStartup(id, updates),
    convertSoftToHard: (id, amt, cid) => useDealflowStore.getState().convertSoftToHard(id, amt, cid),
    closeFundraisingRound: (outcome) => {
      const profileStore = useProfileStore.getState();
      useDealflowStore.getState().closeFundraisingRound(
        outcome,
        profileStore.startupProfile,
        profileStore.setStartupProfile,
      );
    },

    // ---- Reset all domain stores ----
    // INVARIANT: every persisted, user-scoped store must be reset here so no
    // user's data survives sign-out into the next session on the same device.
    // The completeness guard in __tests__/signout-store-reset.test.ts fails if a
    // persisted store is added without being wired in below.
    reset: () => {
      // src/lib/state/ domain stores
      useProfileStore.getState().reset();
      useScoreStore.getState().reset();
      useDealflowStore.getState().reset();
      useMessagingStore.getState().reset();
      useDataroomStore.getState().reset();
      useSubscriptionStore.getState().reset();
      useScoreHistoryStore.getState().reset();
      useUsageStore.getState().reset();
      // feature-folder stores (cloud-storage stores also have their SecureStore
      // OAuth tokens purged in AuthProvider.signOut — reset() only clears state)
      useReferralStore.getState().reset();
      useTeamStore.getState().reset();
      // (shared-dd is now DB-backed server state — its React Query cache is
      //  cleared on sign-out via queryClient.clear() in AuthProvider.signOut)
      useThesisStore.getState().reset();
      useDataRoomStore.getState().resetAll();
      useGoogleDriveStore.getState().reset();
      useDropboxStore.getState().reset();
    },
  })
);

// ============================================================================
// Hydration selector (backward compat)
// ============================================================================

/**
 * Checks whether all persisted domain stores have finished rehydrating.
 *
 * FIX (issue #13): The old monolithic store set _hasHydrated synchronously
 * before async mock data imports resolved.  Each domain store now manages
 * its own _hasHydrated flag and only sets it after ALL async operations
 * (including mock data imports) have completed.  This hook subscribes
 * to both persisted stores via Zustand selectors (returning primitives)
 * and returns true only when both are fully ready.
 */
export const useHasHydrated = () => {
  const profileHydrated = useProfileStore((s) => s._hasHydrated);
  const dealflowHydrated = useDealflowStore((s) => s._hasHydrated);
  return profileHydrated && dealflowHydrated;
};
